import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PRODUCT_NOTIFICATIONS_TABLE } from "../../constants/FirestoreConstant";

/**
 * Meldingen over producten (BMX-49). Vandaag is er één soort: een uitlopend product waarvan de
 * voorraad onder de bestelgrens is gezakt.
 *
 * De meldingen worden geschreven door een Cloud Function (`onProductDiscontinuedStock`, plus een
 * dagelijkse controle), niet hier berekend. Dat is bewust: zo bestaat een melding als document,
 * met geschiedenis, en is "gelezen" iets dat over apparaten heen klopt in plaats van iets dat in
 * de localStorage van één browser hangt.
 *
 * Eigen collectie en niet purchaseOrderNotifications: die hangt aan een inkooporder en achter
 * inkooprechten, terwijl dit een producteigenschap is die achter productrechten hoort.
 *
 * @param {Object} options
 * @param {boolean} options.onlyUnresolved - alleen openstaande meldingen (standaard true)
 * @param {boolean} options.enabled - op false slaat de listener helemaal over (bijv. een
 *   gebruiker zonder productrechten)
 * @returns {{ notifications: Array, loading: boolean, error: any, markResolved: Function, markRead: Function }}
 */
export const useProductNotifications = (options = {}) => {
  const { onlyUnresolved = true, enabled = true } = options;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    let query = firestore.collection(FIRESTORE_PRODUCT_NOTIFICATIONS_TABLE);
    if (onlyUnresolved) {
      query = query.where("resolvedAt", "==", null);
    }
    query = query.orderBy("createdAt", "desc").limit(50);

    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        setNotifications(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching product notifications:", err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firestore, onlyUnresolved, enabled]);

  const markResolved = async (notificationId, resolvedByEmail) => {
    try {
      await firestore.collection(FIRESTORE_PRODUCT_NOTIFICATIONS_TABLE).doc(notificationId).update({
        resolvedAt: Math.floor(Date.now() / 1000),
        resolvedBy: resolvedByEmail,
      });
    } catch (err) {
      console.error("Error resolving product notification:", err);
    }
  };

  const markRead = async (notificationId, uid) => {
    try {
      const doc = await firestore.collection(FIRESTORE_PRODUCT_NOTIFICATIONS_TABLE).doc(notificationId).get();
      const readBy = doc.data()?.readBy || [];
      if (!readBy.includes(uid)) {
        await firestore.collection(FIRESTORE_PRODUCT_NOTIFICATIONS_TABLE).doc(notificationId).update({
          readBy: [...readBy, uid],
        });
      }
    } catch (err) {
      console.error("Error marking product notification as read:", err);
    }
  };

  return { notifications, loading, error, markResolved, markRead };
};
